import { Crown, Spade } from 'lucide-react';
import PremiumCasinoChip from 'components/PremiumCasinoChip';

export type VariantTheme = 'green' | 'purple' | 'red';

interface VariantCardProps {
  title: string;
  subtitle: string;
  description: string;
  theme: VariantTheme;
  cardLabels: string[];
  chipSymbol?: 'spade' | 'crown';
  tableThemeColor?: string;
  cardBackStyle?: string;
  active?: boolean;
  loading?: boolean;
  onPlay: () => void;
}

function MiniCard({ label, offset }: { label: string; offset: number }) {
  const isRed = label.includes('♥');
  return (
    <div
      className="provider-variant-card-face absolute rounded-md border border-white/20 bg-gradient-to-b from-white to-gray-200 shadow-lg"
      style={{
        width: 52,
        height: 72,
        left: offset,
        transform: `rotate(${-8 + offset * 0.15}deg)`,
        zIndex: offset,
      }}
    >
      <div className="flex h-full flex-col items-center justify-center p-1">
        <span className={`text-lg font-bold ${isRed ? 'text-red-600' : 'text-gray-900'}`}>{label.replace(/[♥♠♣♦]/g, '')}</span>
        <span className={`text-sm ${isRed ? 'text-red-500' : 'text-gray-800'}`}>{label.match(/[♥♠♣♦]/)?.[0] ?? '♠'}</span>
      </div>
    </div>
  );
}

export default function VariantCard({
  title,
  subtitle,
  description,
  theme,
  cardLabels,
  chipSymbol = 'spade',
  tableThemeColor,
  cardBackStyle,
  active = true,
  loading,
  onPlay,
}: VariantCardProps) {
  const ChipIcon = chipSymbol === 'crown' ? Crown : Spade;

  return (
    <article className={`provider-variant-card provider-variant-card--${theme} ${!active ? 'provider-variant-card--inactive' : ''}`}>
      <header className="provider-variant-card-header">
        <h2 className="provider-variant-card-title">{title}</h2>
        <p className="provider-variant-card-subtitle">- {subtitle} -</p>
      </header>

      <div
        className={`provider-variant-card-body provider-variant-card-body--${theme}`}
        style={tableThemeColor ? { background: `linear-gradient(165deg, ${tableThemeColor} 0%, rgba(0,0,0,0.55) 100%)` } : undefined}
        data-card-back={cardBackStyle || undefined}
      >
        <div className="provider-variant-card-art">
          <div className="relative h-[88px] w-[140px]">
            {cardLabels.map((label, i) => (
              <MiniCard key={label} label={label} offset={i * 28} />
            ))}
          </div>
          <div className="provider-variant-chip-stack">
            <PremiumCasinoChip size={44} variant={theme === 'purple' ? 4 : theme === 'red' ? 2 : 0} />
            <div className="-mt-4">
              <PremiumCasinoChip size={40} variant={theme === 'purple' ? 5 : theme === 'red' ? 3 : 1} />
            </div>
            <div className="provider-variant-chip-symbol">
              <ChipIcon size={14} className="text-tp-gold" fill="currentColor" />
            </div>
          </div>
        </div>
      </div>

      <p className="provider-variant-card-desc">{description}</p>

      {active ? (
        <button
          type="button"
          className="provider-play-btn"
          disabled={loading}
          onClick={onPlay}
        >
          {loading ? (
            <>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#1a1208]/30 border-t-[#1a1208]" />
              JOINING…
            </>
          ) : (
            <>
              <span className="provider-play-btn-diamond" aria-hidden>◆</span>
              PLAY NOW
              <span className="provider-play-btn-diamond" aria-hidden>◆</span>
            </>
          )}
        </button>
      ) : (
        <div className="provider-coming-soon" aria-label="Coming soon">
          COMING SOON
        </div>
      )}
    </article>
  );
}
